/**
 * Fonte de dados em Firestore + callables.
 *
 * Divisão de trabalho, ditada pelas Security Rules (`firestore.rules`):
 *
 *  - **direto do cliente:** criar, ler, editar o draft, apagar. As Rules garantem que cada dono só
 *    alcança os seus.
 *  - **por callable:** publicar e mexer em segredos. `published` é imutável pelo cliente porque só
 *    a Function revalida o schema no servidor; e o valor de um segredo nunca pode ser legível.
 *
 * Por isso `saveDraft` jamais inclui `published`, `secretNames`, `publicKey` ou `ownerId` no
 * update: qualquer um deles faria a regra recusar a escrita inteira.
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit as limitTo,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  type DocumentData,
} from 'firebase/firestore';
import { httpsCallable, type FunctionsError } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import {
  createDefaultAppearance,
  createStarterFlow,
  type WebchatVersion,
} from '@merlin/flow-engine';
import { getAuthClient, getDb, getFunctionsClient, getStorageClient } from '../lib/firebase.js';
import {
  generatePublicKey,
  PublishBlockedError,
  type AnalyticsDay,
  type LeadEntry,
  type VersionEntry,
  type DataSource,
  type DraftPatch,
  type MonthlyUsage,
  type PublishedVersion,
  type WebchatDoc,
  type WebchatSummary,
} from './types.js';

function requireUid(): string {
  const uid = getAuthClient().currentUser?.uid;
  if (!uid) throw new Error('Sessão expirada. Entre novamente.');
  return uid;
}

function millis(value: unknown): number | null {
  if (value instanceof Timestamp) return value.toMillis();
  if (typeof value === 'number') return value;
  return null;
}

function toDoc(id: string, data: DocumentData): WebchatDoc {
  const published = (data.published ?? null) as (PublishedVersion & { publishedAt?: unknown }) | null;
  return {
    id,
    ownerId: String(data.ownerId ?? ''),
    name: String(data.name ?? 'Webchat sem nome'),
    publicKey: String(data.publicKey ?? ''),
    authorizedDomains: Array.isArray(data.authorizedDomains) ? (data.authorizedDomains as string[]) : [],
    outboundAllowlist: Array.isArray(data.outboundAllowlist) ? (data.outboundAllowlist as string[]) : [],
    secretNames: Array.isArray(data.secretNames) ? (data.secretNames as string[]) : [],
    ...(typeof data.collectLeads === 'boolean' ? { collectLeads: data.collectLeads } : {}),
    // Ausente e `null` significam coisas diferentes (padrão vs. ilimitado): preserve os dois.
    ...(typeof data.status === 'string' ? { status: data.status } : {}),
    ...(data.requestLimit === null
      ? { requestLimit: null }
      : typeof data.requestLimit === 'number'
        ? { requestLimit: data.requestLimit }
        : {}),
    ...(data.conversationLimit === null
      ? { conversationLimit: null }
      : typeof data.conversationLimit === 'number'
        ? { conversationLimit: data.conversationLimit }
        : {}),
    draft: (data.draft ?? {
      appearance: createDefaultAppearance(),
      flow: createStarterFlow(),
    }) as WebchatVersion,
    published:
      published && published.flow
        ? { appearance: published.appearance, flow: published.flow, version: Number(published.version ?? 1) }
        : null,
    updatedAt: millis(data.updatedAt),
  };
}

/** Extrai a lista de blockers que a Function manda em `details` do `failed-precondition`. */
function blockersFrom(error: unknown): string[] | null {
  const details = (error as FunctionsError | undefined)?.details;
  if (Array.isArray(details)) return details.map((d) => String(d));
  return null;
}

export function createFirestoreSource(): DataSource {
  const db = getDb();

  return {
    kind: 'firestore',

    async list(): Promise<WebchatSummary[]> {
      const uid = requireUid();
      const snapshot = await getDocs(
        query(collection(db, 'webchats'), where('ownerId', '==', uid), orderBy('updatedAt', 'desc')),
      );
      return snapshot.docs.map((d) => {
        const parsed = toDoc(d.id, d.data());
        return {
          id: parsed.id,
          name: parsed.name,
          publicKey: parsed.publicKey,
          publishedVersion: parsed.published?.version ?? null,
          updatedAt: parsed.updatedAt,
        };
      });
    },

    async get(id: string): Promise<WebchatDoc | null> {
      const snapshot = await getDoc(doc(db, 'webchats', id));
      return snapshot.exists() ? toDoc(snapshot.id, snapshot.data()) : null;
    },

    async create(name: string): Promise<string> {
      const uid = requireUid();
      // A forma aqui tem que casar com `validShapeOnCreate` das Rules: ownerId do próprio usuário,
      // `published` nulo e `secretNames` vazio.
      const created = await addDoc(collection(db, 'webchats'), {
        ownerId: uid,
        name: name.trim() || 'Webchat sem nome',
        publicKey: generatePublicKey(),
        authorizedDomains: ['localhost'],
        outboundAllowlist: [],
        secretNames: [],
        draft: { appearance: createDefaultAppearance(), flow: createStarterFlow() },
        published: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return created.id;
    },

    async saveDraft(id: string, patch: DraftPatch): Promise<void> {
      const current = await this.get(id);
      if (!current) throw new Error('Webchat não encontrado.');

      await updateDoc(doc(db, 'webchats', id), {
        ...(patch.name !== undefined ? { name: patch.name } : {}),
        ...(patch.authorizedDomains !== undefined ? { authorizedDomains: patch.authorizedDomains } : {}),
        ...(patch.outboundAllowlist !== undefined ? { outboundAllowlist: patch.outboundAllowlist } : {}),
        ...(patch.collectLeads !== undefined ? { collectLeads: patch.collectLeads } : {}),
        ...(patch.conversationLimit !== undefined ? { conversationLimit: patch.conversationLimit } : {}),
        ...(patch.requestLimit !== undefined ? { requestLimit: patch.requestLimit } : {}),
        draft: {
          appearance: patch.appearance ?? current.draft.appearance,
          flow: patch.flow ?? current.draft.flow,
        },
        updatedAt: serverTimestamp(),
      });
    },

    async publish(id: string): Promise<{ version: number; unchanged: boolean }> {
      try {
        const callable = httpsCallable<
          { webchatId: string },
          { version: number; unchanged?: boolean }
        >(getFunctionsClient(), 'publishWebchat');
        const result = await callable({ webchatId: id });
        return { version: Number(result.data.version), unchanged: result.data.unchanged === true };
      } catch (error) {
        const blockers = blockersFrom(error);
        if (blockers?.length) {
          throw new PublishBlockedError('Corrija antes de publicar.', blockers);
        }
        throw error;
      }
    },

    async remove(id: string): Promise<void> {
      await deleteDoc(doc(db, 'webchats', id));
    },

    async upsertSecret(id: string, name: string, value: string): Promise<void> {
      const callable = httpsCallable<{ webchatId: string; name: string; value: string }, { name: string }>(
        getFunctionsClient(),
        'upsertSecret',
      );
      await callable({ webchatId: id, name, value });
    },

    async deleteSecret(id: string, name: string): Promise<void> {
      const callable = httpsCallable<{ webchatId: string; name: string }, { name: string }>(
        getFunctionsClient(),
        'deleteSecret',
      );
      await callable({ webchatId: id, name });
    },

    async uploadButtonImage(id: string, file: File): Promise<string> {
      const uid = requireUid();
      // O caminho carrega o uid porque as regras do Storage não conseguem consultar o Firestore —
      // a posse é provada pela própria estrutura do path (ver storage.rules).
      const extension = (file.name.split('.').pop() ?? 'png').toLowerCase().replace(/[^a-z0-9]/g, '');
      const target = ref(getStorageClient(), `webchat-images/${uid}/${id}/button-${Date.now()}.${extension}`);
      await uploadBytes(target, file, { contentType: file.type });
      return getDownloadURL(target);
    },

    async listLeads(id: string, max = 100): Promise<LeadEntry[]> {
      const snapshot = await getDocs(
        query(collection(db, 'webchats', id, 'leads'), orderBy('createdAt', 'desc'), limitTo(max)),
      );
      return snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          variables: (data.variables ?? {}) as Record<string, unknown>,
          transcript: Array.isArray(data.transcript) ? (data.transcript as LeadEntry['transcript']) : [],
          ...(typeof data.pageUrl === 'string' ? { pageUrl: data.pageUrl } : {}),
          ...(typeof data.sdkVersion === 'string' ? { sdkVersion: data.sdkVersion } : {}),
          createdAt: millis(data.createdAt),
        };
      });
    },

    async deleteLead(id: string, leadId: string): Promise<void> {
      // As Rules liberam `delete` ao dono de propósito: atender pedido de exclusão é obrigação dele.
      await deleteDoc(doc(db, 'webchats', id, 'leads', leadId));
    },

    async listVersions(id: string, max = 20): Promise<VersionEntry[]> {
      /*
       * Ordena pelo **campo** `version`, não pelo id do documento: id é string, e ordenação lexical
       * colocaria a v10 antes da v2.
       */
      const snapshot = await getDocs(
        query(collection(db, 'webchats', id, 'versions'), orderBy('version', 'desc'), limitTo(max)),
      );
      return snapshot.docs.map((d) => {
        const data = d.data();
        return {
          version: Number(data.version ?? Number(d.id)),
          publishedAt: millis(data.publishedAt),
          nodeCount: Number(data.nodeCount ?? 0),
        };
      });
    },

    async getVersion(id: string, version: number): Promise<WebchatVersion | null> {
      const snapshot = await getDoc(doc(db, 'webchats', id, 'versions', String(version)));
      if (!snapshot.exists()) return null;
      const data = snapshot.data();
      if (!data.flow || !data.appearance) return null;
      return { appearance: data.appearance as WebchatVersion['appearance'], flow: data.flow as WebchatVersion['flow'] };
    },

    async getAnalytics(id: string, days = 14): Promise<AnalyticsDay[]> {
      // O id do documento é a data (AAAA-MM-DD), então ordenar por nome já é ordenar por dia.
      const snapshot = await getDocs(
        query(collection(db, 'webchats', id, 'analytics'), orderBy('day', 'desc'), limitTo(days)),
      );
      return snapshot.docs.map((d) => {
        const data = d.data();
        return {
          day: String(data.day ?? d.id),
          starts: Number(data.starts ?? 0),
          completions: Number(data.completions ?? 0),
          abandons: Number(data.abandons ?? 0),
          nodeEnters: (data.nodeEnters ?? {}) as Record<string, number>,
          nodeAbandons: (data.nodeAbandons ?? {}) as Record<string, number>,
        };
      });
    },

    /* Uma leitura para os dois contadores: eles moram no mesmo documento, como no servidor. */
    async getMonthlyUsage(id: string, month: string): Promise<MonthlyUsage> {
      const dados = (await getDoc(doc(db, 'webchats', id, 'usage', month))).data();
      return {
        conversations: Number((dados?.conversations as number | undefined) ?? 0),
        requests: Number((dados?.requests as number | undefined) ?? 0),
      };
    },
  };
}
